import { action, computed, makeObservable, observable, when } from 'mobx'
import { RootStore } from './RootStore'
import { StoreModel } from './utils'
import { CommerceAPI } from 'commerce-api'
import { DeliveryTypes } from '../types/store/common'
import { formatNumberToCurrency } from '../utils/utils'

const POSTCODE_WAIT_TIMEOUT = 10000

export interface DeliveryType {
    deliveryType: DeliveryTypes[keyof DeliveryTypes]
    available: boolean
    lowestCost: number | null
    unavailableMessage: string | null
    deliveryTypeSubtext: string
    features: string[]
}

export class DeliveryStore implements StoreModel {
    api: CommerceAPI
    rootStore: RootStore
    isPreBookEnabled: boolean
    isPreBookForNewCustomersEnabled: boolean
    pendingAvailability: Promise<void> | null = null

    constructor(
        rootStore: RootStore,
        initialState?: Pick<DeliveryStore, 'isPreBookEnabled' | 'isPreBookForNewCustomersEnabled'>
    ) {
        this.api = rootStore.api
        this.rootStore = rootStore
        this.isPreBookEnabled = initialState?.isPreBookEnabled ?? false
        this.isPreBookForNewCustomersEnabled = initialState?.isPreBookForNewCustomersEnabled ?? false

        makeObservable(this, {
            isPreBookEnabled: observable,
            isPreBookForNewCustomersEnabled: observable,
            availableDeliveryTypes: computed,
            minDeliveryThreshold: computed,
            setPreBookEnabled: action.bound,
            setPreBookForNewCustomersEnabled: action.bound,
            loadAvailability: action.bound
        })
    }

    get minDeliveryThreshold(): number | null {
        return (this.rootStore.globalStore.customSitePreferences['minimumDeliveryValue'] as number) ?? null
    }

    get availableDeliveryTypes(): DeliveryType[] {
        const { deliverySlotArray, sameDayDeliveryAvailable, isAppliedPostCodeDeliverable } = this.rootStore.basketStore
        const { preBookDeliverySlotArray, preBookDeliverySlots, preBookDays, preBookSeasonalName, prebookedOrder } =
            this.rootStore.preBookStore
        const { 
            isBasketOverRapidDeliveryItemLimit,
            rapidDeliveryMaxItems,
            isRapidDeliveryAvailableForPostcode,
            rapidDeliveryMinDelivery,
            rapidDeliveryDeliveryEstimate,
            rapidDeliveryShippingCost,
            isBasketOverRapidDeliveryWeightLimit
        } = this.rootStore.rapidDeliveryStore
        const standardDeliveryAvailable = isAppliedPostCodeDeliverable && deliverySlotArray.some((day) => day.hasAvailableSlots)
        const preBookDatesReturned = Object.keys(preBookDeliverySlots || {}).length > 0
        const preBookDeliveryAvailable =
            this.isPreBookEnabled &&
            this.isPreBookForNewCustomersEnabled &&
            isAppliedPostCodeDeliverable === true &&
            preBookDatesReturned &&
            preBookDeliverySlotArray.some((day) => day.hasAvailableSlots)
        const getSlotCost = (slot: (typeof deliverySlotArray)[number]['slots'][number]) =>
            slot.hasFreeDelivery ? 0 : slot.shippingCost
        const standardCosts = deliverySlotArray.flatMap((day) =>
            day.hasAvailableSlots ? day.slots.filter((slot) => slot.available).map(getSlotCost) : []
        )
        const sameDayCosts = (deliverySlotArray[0]?.slots || [])
            .filter((slot) => slot.available)
            .map(getSlotCost)
        const preBookCosts = preBookDeliverySlotArray.flatMap((day) =>
            day.hasAvailableSlots
                ? day.slots
                    .filter((slot) => slot.available)
                    .map((slot) => slot.preBookShippingCost ?? slot.shippingCost)
                : []
        )
        const lowest = (costs: number[]) => (costs.length ? Math.min(...costs) : null)

        return [
            {
                deliveryType: DeliveryTypes.RAPID,
                available:
                    isRapidDeliveryAvailableForPostcode 
                    && !isBasketOverRapidDeliveryItemLimit 
                    && !isBasketOverRapidDeliveryWeightLimit
                    || false,
                lowestCost: rapidDeliveryShippingCost,
                unavailableMessage:
                    isBasketOverRapidDeliveryItemLimit || isBasketOverRapidDeliveryWeightLimit
                    ? 'Your current basket is over the limit for Rapid Delivery'
                    : null,
                deliveryTypeSubtext: `Delivery in under ${rapidDeliveryDeliveryEstimate ?? 60} minutes`,
                features: [
                    'Delivered in under 1 hour',
                    `Up to ${rapidDeliveryMaxItems} items per order`,
                    `Minimum ${formatNumberToCurrency(rapidDeliveryMinDelivery || 0, 'GBP', 0, 2)} spend per order`
                ]
            },
            {
                deliveryType: DeliveryTypes.SAMEDAY,
                available: sameDayDeliveryAvailable || false,
                lowestCost: lowest(sameDayCosts),
                unavailableMessage: null,
                deliveryTypeSubtext: 'Get your shop delivered today',
                features: [
                    'Need it today? Get it today',
                    'Flexible 2-hour delivery slots',
                    'No basket limits'
                ]
            },
            {
                deliveryType: DeliveryTypes.STANDARD,
                available: standardDeliveryAvailable || false,
                lowestCost: lowest(standardCosts),
                unavailableMessage: null,
                deliveryTypeSubtext: "1000's of delivery slots to suit you",
                features: [
                    'Next Day Delivery',
                    'Flexible 2 hour delivery slots',
                    'Book up to 7 days in advance'
                ]
            },
            {
                deliveryType: DeliveryTypes.PREBOOK,
                available: preBookDeliveryAvailable || false,
                lowestCost: lowest(preBookCosts),
                unavailableMessage: null,
                deliveryTypeSubtext: `Pre-book your ${preBookSeasonalName && `${preBookSeasonalName} `}delivery up to ${preBookDays} days in advance`,
                features: [
                    `Pre-book slots up to ${preBookDays} days in advance`,
                    'Plan ahead for holidays and parties',
                    'Secure your delivery slot'
                ]
            }
        ].filter(
            (delivery) => {
                const RapidAvailable = delivery.deliveryType !== 'Rapid Delivery' || isRapidDeliveryAvailableForPostcode
                const PreBookAvailable = delivery.deliveryType !== 'Pre-Book Delivery' || !prebookedOrder
                // Filter out unavailable delivery options
                return RapidAvailable && PreBookAvailable
            }
        )
    }

    get asJson() {
        return {
            isPreBookEnabled: this.isPreBookEnabled,
            isPreBookForNewCustomersEnabled: this.isPreBookForNewCustomersEnabled
        }
    }

    setPreBookEnabled(enabled: boolean) {
        this.isPreBookEnabled = enabled
    }

    setPreBookForNewCustomersEnabled(enabled: boolean) {
        this.isPreBookForNewCustomersEnabled = enabled
    }

    async loadAvailability(): Promise<void> {
        if (this.pendingAvailability) return this.pendingAvailability

        this.pendingAvailability = this.fetchAvailability().finally(() => {
            this.pendingAvailability = null
        })

        return this.pendingAvailability
    }

    private async fetchAvailability(): Promise<void> {
        const { basketStore, customerStore, globalStore, preBookStore } = this.rootStore

        if (!basketStore.basket?.basketId) return

        // The postcode resolves asynchronously, so wait for it before fetching anything that depends on it
        if (!basketStore.deliveryPostalCode) {
            try {
                await when(() => Boolean(basketStore.deliveryPostalCode), {
                    timeout: POSTCODE_WAIT_TIMEOUT
                })
            } catch (error) {
                console.error('Timed out waiting for a delivery postcode', error)
            }
        }

        const postcode = basketStore.deliveryPostalCode

        await (basketStore.getShippingMethods() as unknown as Promise<unknown>)

        if (postcode) {
            await (basketStore.getDeliverySlots() as unknown as Promise<unknown>)
        }

        await (globalStore.getCustomSitePreferenceById('preBookEnabled') as unknown as Promise<unknown>)
        await (globalStore.getCustomSitePreferenceById(
            'preBookForNewCustomersEnabled'
        ) as unknown as Promise<unknown>)
        this.setPreBookEnabled(Boolean(globalStore.customSitePreferences.preBookEnabled))
        this.setPreBookForNewCustomersEnabled(
            Boolean(globalStore.customSitePreferences.preBookForNewCustomersEnabled)
        )

        if (this.isPreBookEnabled && postcode) {
            // Loading the customer can trigger resetPreBook(), which clears the pre-book postcode mid-fetch
            if (customerStore.loading) {
                try {
                    await when(() => !customerStore.loading, { timeout: POSTCODE_WAIT_TIMEOUT })
                } catch (error) {
                    console.error('Timed out waiting for the customer to load', error)
                }
            }

            try {
                await (preBookStore.setPreBookPostalCode(postcode) as unknown as Promise<unknown>)
                await (preBookStore.getPreBookDeliverySlots() as unknown as Promise<unknown>)
            } catch (error) {
                console.error('Error fetching pre-book delivery slots', error)
            }
        }
    }
}
